import { ArrowUpRight, Check, Hand, UserPlus, X } from '@orgus/ui/icons';
import { useState } from 'react';
import { parseMentions } from '@orgus/protocol';
import type { Assignment, TeamsState } from '@orgus/protocol';
import { Badge, Button, Popover, PopoverContent, PopoverTrigger, RelativeTime, Tooltip, cn, toast } from '@orgus/ui';
import { useCan } from '../auth/useMe.ts';
import { teamsClient } from './client.ts';
import { MentionInput } from './MentionInput.tsx';
import { REASON_LABEL, nameOf, personOf, useMyLogin, useTeams } from './model.ts';
import { AvatarStack } from './PresenceAvatars.tsx';

/** "You and Devon Ortiz", "Maya Chen", "Devon Ortiz and 2 others". */
export function assigneeText(state: TeamsState | null, a: Pick<Assignment, 'assignees'>, me: string | null): string {
  const names = a.assignees.map((l) => nameOf(state, l, me));
  if (names.length <= 2) return names.join(' and ');
  return `${names[0]} and ${names.length - 1} others`;
}

/** The text without its @mentions: what is left is the note. */
export function noteOf(text: string): string {
  return text.replace(/(^|\s)@[a-z0-9._-]+/gi, ' ').replace(/\s+/g, ' ').trim();
}

function AssignmentSummary({ a, state, me }: { a: Assignment; state: TeamsState | null; me: string | null }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <AvatarStack people={a.assignees.map((l) => personOf(state, l))} max={4} />
        <span className="min-w-0 flex-1 truncate text-base font-medium text-fg">{assigneeText(state, a, me)}</span>
        <Badge tone={a.reason === 'escalated' ? 'waiting' : a.reason === 'claimed' ? 'passed' : 'neutral'} icon={a.reason === 'escalated' ? <ArrowUpRight /> : undefined}>
          {REASON_LABEL[a.reason]}
        </Badge>
      </div>
      <p className="text-sm text-fg-muted">
        {a.why} <RelativeTime ts={a.at} className="text-fg-subtle" />
      </p>
      {a.note && <p className="rounded-md bg-hover px-2 py-1.5 text-sm text-fg">{a.note}</p>}
      {a.ackedBy && (
        <p className="flex items-center gap-1.5 text-sm text-passed">
          <Check className="size-3.5" aria-hidden /> {nameOf(state, a.ackedBy, me)} {a.ackedBy === me ? 'are' : 'is'} on it
        </p>
      )}
    </div>
  );
}

/**
 * Who holds a Needs-you item, and @mention-style assignment in place: type @person or @team,
 * add a few words, Enter. Operators assign, claim, acknowledge and clear; viewers see who has it.
 */
export function AssignControl({ itemKey, title, projectId, className }: { itemKey: string; title: string; projectId: string | null; className?: string }) {
  const { status, data } = useTeams();
  const me = useMyLogin();
  const canAct = useCan('operator');
  const isAdmin = useCan('admin');
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  if (status !== 'ready' || !data) return null;
  const a = data.assignments.find((x) => x.key === itemKey);
  const mine = !!(me && a?.assignees.includes(me));
  const canClear = !!a && canAct && (mine || a.by === me || isAdmin);

  const run = async (what: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(what);
    try {
      await fn();
      toast.success(done);
      setOpen(false);
      setText('');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  const submit = () => {
    const mentions = parseMentions(text);
    if (!mentions.length) {
      setError('Name someone with @, for example @devon or @platform.');
      return;
    }
    setError(null);
    const note = noteOf(text);
    void run('assign', () => teamsClient.assign({ key: itemKey, title, projectId, to: mentions, ...(note ? { note } : {}) }), `Assigned to ${mentions.map((m) => `@${m}`).join(', ')}`);
  };

  const trigger = a ? (
    <button
      type="button"
      aria-label={`Assigned to ${assigneeText(data, a, me)}${a.reason === 'escalated' ? ', escalated' : ''}. ${canAct ? 'Change the assignment.' : ''}`}
      className={cn(
        'flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-1.5 text-sm transition-colors',
        mine ? 'border-accent-line bg-accent-soft text-fg' : 'border-border-subtle text-fg-muted hover:border-border hover:text-fg',
        className,
      )}
    >
      <AvatarStack people={a.assignees.map((l) => personOf(data, l))} max={2} size={18} />
      {a.reason === 'escalated' && <ArrowUpRight className="size-3.5 text-waiting" aria-hidden />}
      <span className="hidden max-w-[110px] truncate lg:inline">{mine ? 'You' : (personOf(data, a.assignees[0]!).name.split(' ')[0] ?? '')}{a.assignees.length > 1 && !mine ? ` +${a.assignees.length - 1}` : ''}</span>
    </button>
  ) : canAct ? (
    <button
      type="button"
      aria-label="Assign this to someone"
      className={cn('flex size-7 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-hover hover:text-fg', className)}
    >
      <UserPlus className="size-4" aria-hidden />
    </button>
  ) : null;

  if (!trigger) return null;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setError(null);
      }}
    >
      <Tooltip content={a ? `${REASON_LABEL[a.reason]}: ${assigneeText(data, a, me)}` : 'Assign'}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      </Tooltip>
      <PopoverContent aria-label={a ? 'Assignment' : 'Assign'} className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
        {a ? <AssignmentSummary a={a} state={data} me={me} /> : <p className="text-base font-medium text-fg">Assign this item</p>}
        {canAct ? (
          <>
            <MentionInput
              state={data}
              value={text}
              onChange={(v) => {
                setText(v);
                setError(null);
              }}
              onSubmit={submit}
              label={a ? 'Reassign to people or a team' : 'Assign to people or a team'}
              placeholder={a ? '@someone to hand it to, and a note' : '@someone, and a note'}
              error={error}
              autoFocus
            />
            <div className="flex flex-wrap items-center gap-2">
              {canClear && (
                <Button size="sm" variant="ghost" icon={<X />} loading={busy === 'clear'} onClick={() => void run('clear', () => teamsClient.unassign(itemKey), 'Assignment cleared')}>
                  Clear
                </Button>
              )}
              <span className="ml-auto flex items-center gap-2">
                {mine && !a?.ackedBy && (
                  <Button size="sm" icon={<Check />} loading={busy === 'ack'} onClick={() => void run('ack', () => teamsClient.ack(itemKey), 'Marked as yours. Escalation stops.')}>
                    On it
                  </Button>
                )}
                {me && !(mine && a?.assignees.length === 1) && (
                  <Button size="sm" icon={<Hand />} loading={busy === 'claim'} onClick={() => void run('claim', () => teamsClient.claim(itemKey, title, projectId), 'It is yours now')}>
                    Take it
                  </Button>
                )}
                <Button size="sm" variant="primary" loading={busy === 'assign'} onClick={submit}>
                  Assign
                </Button>
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-fg-subtle">Viewers can see who has an item. Operators and admins assign them.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
